import { SupportChatCategory, SupportChatPriority } from "../../../common/enums";

/**
 * Shapes of the AiAssistant API (docs/ai-assistant.md) as the page uses them. The raw responses
 * go through the normalizers in aiAssistantNormalizers.ts first: queries arrive camelCased,
 * mutations and the stream do not, and enums may come as names or as numbers.
 */

export const AI_DEFAULT_MAX_IMAGES_PER_MESSAGE = 4;
export const AI_DEFAULT_MAX_MESSAGE_CHARS = 4000;
export const AI_DEFAULT_MAX_IMAGE_BYTES = 3_500_000;

/** The long side Claude reads an image at; anything bigger only costs upload time. */
export const AI_MAX_IMAGE_SIDE_PX = 1568;

/** The character counter shows from this share of the limit on. */
export const AI_CHAR_COUNTER_THRESHOLD = 0.9;

export interface IAiAssistantStatus {
  enabled: boolean;
  usedPercent: number;
  limitReached: boolean;
  /** When the monthly budget starts over (first day of the next UTC month), ISO; null if unknown. */
  resetsOn: string | null;
  maxImagesPerMessage: number;
  maxMessageChars: number;
  maxImageBytes: number;
}

export interface IAiConversationListItem {
  id: string;
  title: string;
  lastMessageAt: string | null;
}

export type AiMessageRole = "user" | "assistant";

export interface IAiMessageImage {
  id: string;
  url: string;
}

export interface IAiSupportDraft {
  title: string;
  category: SupportChatCategory;
  priority: SupportChatPriority;
  content: string;
}

/** A ticket the assistant created itself (create_support_ticket), after the user agreed in the chat. */
export interface IAiSupportTicket {
  /** The support chat's id. */
  id: string;
  title: string;
}

export interface IAiMessage {
  id: number;
  role: AiMessageRole;
  text: string;
  createdOn: string | null;
  isError: boolean;
  images: IAiMessageImage[];
  toolLabels: string[];
  supportDraft: IAiSupportDraft | null;
  supportChatId: string | null;
  /** Set when this answer (or the error row of a turn that failed after it) created a ticket. */
  supportTicket: IAiSupportTicket | null;
}

export interface IAiConversation {
  id: string;
  title: string;
  isFull: boolean;
  messages: IAiMessage[];
}

/** POST AiAssistant/Send */
export interface IAiSendRequest {
  conversationId?: string;
  message: string;
  attachmentIds: string[];
  pagePath?: string;
}

/** POST AiAssistant/CreateSupportTicket */
export interface IAiCreateSupportTicketRequest {
  messageId: number;
  title: string;
  category: SupportChatCategory;
  priority: SupportChatPriority;
  content: string;
}

/** POST AiAssistant/UploadImage */
export interface IAiUploadedImage {
  id: string;
  url: string;
  fileName: string;
  size: number;
}

// ---- Stream events (text/event-stream, one JSON object per "data:" frame) ----

export interface IAiStreamStartEvent {
  type: "start";
  conversationId: string;
  title: string;
  userMessageId: number | null;
}

export interface IAiStreamTextEvent {
  type: "text";
  text: string;
}

export interface IAiStreamToolEvent {
  type: "tool";
  name: string;
  label: string;
}

export interface IAiStreamSupportDraftEvent {
  type: "supportDraft";
  messageId: number | null;
  draft: IAiSupportDraft;
}

/** Comes after the answer (or the error row) is saved: right before "done", or before "error". */
export interface IAiStreamSupportTicketEvent {
  type: "supportTicket";
  messageId: number | null;
  ticket: IAiSupportTicket;
}

export interface IAiStreamDoneEvent {
  type: "done";
  messageId: number | null;
  usedPercent: number | null;
  limitReached: boolean;
  isFull: boolean;
}

export interface IAiStreamErrorEvent {
  type: "error";
  message: string;
  code: string | null;
}

export type AiStreamEvent =
  | IAiStreamStartEvent
  | IAiStreamTextEvent
  | IAiStreamToolEvent
  | IAiStreamSupportDraftEvent
  | IAiStreamSupportTicketEvent
  | IAiStreamDoneEvent
  | IAiStreamErrorEvent;

/** Codes of a 400 the API answers before the stream starts. */
export type AiSendErrorCode =
  | "disabled"
  | "budget_exceeded"
  | "conversation_full"
  | "busy"
  | "invalid"
  | "not_found";

/** How one Send call ended, as far as the browser saw it. */
export type AiSendOutcome =
  /** The stream ended with "done" or "error". */
  | { kind: "completed" }
  /** The stream closed (or the network dropped) without a final event; the server may still finish. */
  | { kind: "interrupted" }
  /** The user pressed stop. */
  | { kind: "aborted" }
  /** A non-2xx answer before any event. */
  | { kind: "rejected"; status: number; message: string | null; code: string | null }
  /** The request never got an answer (offline, DNS, CORS). */
  | { kind: "network" };

// ---- The page's own state ----

/** A message being sent and answered right now, shown until the stored conversation covers it. */
export interface IAiPendingTurn {
  key: string;
  conversationId: string | null;
  /** The stored user message's id, from the "start" event. */
  userMessageId: number | null;
  text: string;
  /** The attached images; their ids are the uploaded attachments' ids. */
  images: IAiMessageImage[];
  answerText: string;
  /** The label of the tool running right now. */
  toolLabel: string | null;
  supportDraft: IAiSupportDraft | null;
  /** The ticket the assistant created; unlike the draft it stays through an "error" (it exists). */
  supportTicket: IAiSupportTicket | null;
  /** The stored answer's id, from "done" (the draft card sends the ticket with it). */
  answerMessageId: number | null;
  phase: "waiting" | "streaming" | "done" | "error" | "stopped";
  errorMessage: string | null;
}

export type AiAssistantBanner = "budget" | "conversationFull" | null;

/** An image picked for the next message: converting, uploading, uploaded or failed. */
export interface IAiComposerAttachment {
  localId: string;
  fileName: string;
  previewUrl: string;
  status: "preparing" | "uploading" | "uploaded" | "failed";
  /** 0..1 while uploading. */
  progress: number;
  uploaded: IAiUploadedImage | null;
}

// ---- Texts ----

export const aiAssistantTexts = {
  title: "AI асистент",
  betaTag: "Beta",
  newConversation: "Нов разговор",
  teamRequests: "Запитвания към екипа",
  conversations: "Разговори",
  noConversations: "Още нямате разговори.",
  untitledConversation: "Нов разговор",
  emptyTitle: "С какво да помогна?",
  emptyIntro:
    "Попитайте каквото и да е — за Planny, за ваш клиент, за статистика, или нещо съвсем друго.",
  inputPlaceholder: "Попитайте нещо...",
  disclaimer: "AI може да греши. Проверявайте важните неща.",
  disclaimerShort: "AI може да греши",
  humanLink: "Предпочитате човек? Пишете на екипа",
  humanLinkShort: "Пишете на екипа",
  retry: "Опитай отново",
  stopped: "Спряхте отговора. Ако вече е готов, ще се появи след малко.",
  interrupted: "Връзката прекъсна. Ако отговорът е готов, ще се появи след малко.",
  networkError: "Няма връзка със сървъра. Проверете интернета и опитайте отново.",
  genericError: "Нещо се обърка и не успях да отговоря. Опитайте отново.",
  tooManyRequests: "Изпращате съобщения твърде бързо. Изчакайте малко.",
  busy: "Предишното съобщение още се обработва. Изчакайте малко.",
  disabled: "AI асистентът не е наличен в момента.",
  notFound: "Разговорът не беше намерен.",
  conversationFull: "Разговорът стана много дълъг. Започнете нов разговор, за да продължим.",
  budgetReachedPrefix: "Месечният лимит на AI асистента за вашия обект е изчерпан.",
  writeToTeam: "Пишете на екипа",
  deleteConversationLabel: "Изтрий разговора",
  deleteConfirmYes: "Изтрий",
  deleteConfirmNo: "Откажи",
  attachImage: "Прикачи изображение",
  removeImage: "Премахни изображението",
  send: "Изпрати",
  stop: "Спри отговора",
  dropHere: "Пуснете изображението тук",
  tooManyImages: (max: number) =>
    `Можете да прикачите до ${max} ${max === 1 ? "изображение" : "изображения"} към едно съобщение.`,
  notAnImage: "Може да прикачвате само изображения.",
  imageTooBig: (fileName: string) =>
    `„${fileName}“ е твърде голямо дори след смаляване. Опитайте с по-малко изображение.`,
  imageUnreadable: (fileName: string) => `„${fileName}“ не може да бъде прочетено като изображение.`,
  imageUploadFailed: (fileName: string) => `„${fileName}“ не беше качено. Опитайте отново.`,
  draftHeader: "Запитване към екипа на Planny",
  draftHint: "Прегледайте текста и го изпратете, когато сте готови.",
  draftSend: "Изпрати към екипа",
  draftSent: "Изпратено",
  draftOpen: "Отвори запитването",
  draftSendFailed: "Запитването не беше изпратено. Опитайте отново.",
  draftContentRequired: "Текстът е задължителен.",
  draftContentTooLong: (max: number) => `Текстът може да е до ${max} символа.`,
  ticketCreated: "Създадох запитване към екипа на Planny",
  imagePreview: "Изображение",
  openImage: "Отвори изображението",
  openConversations: "Покажи разговорите",
} as const;

/** The draft content limit; mirrors DraftSupportTicketTool.MaxContentLength. */
export const AI_SUPPORT_DRAFT_MAX_CONTENT = 4000;
